import { Avatar, Box, CircularProgress, IconButton, InputBase, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { formatTimestamp, getOtherParticipant } from "../messages-utils";

type Props = {
  threadsLoading: boolean;
  filteredThreads: any[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedThreadId: string | null;
  onSelectThread: (threadId: string) => void;
  userId?: string;
  role?: string;
};

const MessagesSidebar = ({
  threadsLoading,
  filteredThreads,
  searchTerm,
  onSearchChange,
  selectedThreadId,
  onSelectThread,
  userId,
  role,
}: Props) => {
  return (
    <Box className="messages-sidebar">
      <Box className="messages-sidebar-header">
        <Box className="messages-search">
          <SearchIcon fontSize="small" />
          <InputBase
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search"
            fullWidth
            inputProps={{ "aria-label": "Search messages" }}
          />
        </Box>
        <IconButton aria-label="Thread options" className="icon-muted">
          <MoreHorizIcon />
        </IconButton>
      </Box>

      <Box className="messages-list">
        {threadsLoading ? (
          <Box className="messages-empty">
            <CircularProgress size={28} />
          </Box>
        ) : !filteredThreads.length ? (
          <Box className="messages-empty">
            <Typography variant="body1" fontWeight={600}>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start a conversation from a profile page.
            </Typography>
          </Box>
        ) : (
          filteredThreads.map((thread: any) => {
            const other = getOtherParticipant(thread, userId, role);
            const lastMessage = thread?.lastMessage;
            const lastBody = lastMessage?.body?.trim() || "";
            const hasAttachments = Array.isArray(lastMessage?.attachments)
              ? lastMessage.attachments.length > 0
              : false;
            const preview =
              lastBody || (hasAttachments ? "Attachment" : "New conversation");
            const timestamp = formatTimestamp(
              thread.lastMessageAt || thread?.lastMessage?.createdAt
            );
            const unreadCount = thread.unreadCount || 0;

            return (
              <Box
                key={thread._id}
                className={`message-thread-item${
                  thread._id === selectedThreadId ? " is-active" : ""
                }`}
                onClick={() => onSelectThread(thread._id)}
              >
                <Avatar
                  src={other?.avatarUrl || undefined}
                  alt={other?.displayName || "User"}
                />
                <Box className="message-thread-content">
                  <Box className="message-thread-meta">
                    <Typography className="message-thread-name">
                      {other?.displayName || "Unknown"}
                    </Typography>
                    <Typography className="message-thread-time">
                      {timestamp}
                    </Typography>
                  </Box>
                  <Box className="message-thread-meta">
                    <Typography className="message-thread-preview">
                      {preview}
                    </Typography>
                    {unreadCount > 0 && (
                      <span className="message-thread-badge">
                        {unreadCount}
                      </span>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default MessagesSidebar;
